import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Loader2 } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { discardImport, unfinishedImportsQueryOptions } from './api'
import type { UnfinishedImport } from './types'

// The Equipment stage's unfinished imports (USA-1337): pick a review up where it was left, or
// discard it. A file still being read shows as reading until its result is saved.

const READING_REFRESH_MS = 5_000

function ImportRow({
  item,
  discarding,
  onResume,
  onDiscard,
}: {
  item: UnfinishedImport
  discarding: boolean
  onResume: () => void
  onDiscard: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const reading = item.status === 'awaiting_upload'
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-lg border bg-muted text-muted-foreground">
        <FileText aria-hidden="true" className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold [overflow-wrap:anywhere]">{item.original_file_name}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          {reading ? (
            <>
              <Loader2 aria-hidden="true" className="size-3 animate-spin motion-reduce:animate-none" />
              Reading
            </>
          ) : (
            <span>
              <strong className="font-medium tabular-nums text-foreground">
                {item.reviewed_count} of {item.row_count}
              </strong>{' '}
              reviewed
            </span>
          )}
        </p>
      </div>
      {confirming ? (
        <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
          <span className="text-sm">Discard this import?</span>
          <Button disabled={discarding} onClick={() => setConfirming(false)} size="sm" type="button" variant="ghost">
            Keep
          </Button>
          <LoadingButton loading={discarding} loadingText="Discarding…" onClick={onDiscard} size="sm" type="button" variant="destructive">
            Discard
          </LoadingButton>
        </div>
      ) : (
        <div className="flex w-full justify-end gap-2 sm:w-auto">
          <Button
            aria-label={`Discard ${item.original_file_name}`}
            onClick={() => setConfirming(true)}
            size="sm"
            type="button"
            variant="ghost"
          >
            Discard
          </Button>
          <Button aria-label={`Resume ${item.original_file_name}`} onClick={onResume} size="sm" type="button">
            Resume
          </Button>
        </div>
      )}
    </li>
  )
}

export function UnfinishedImports({ quoteId, onResume }: { quoteId: string; onResume: (importId: string) => void }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const options = unfinishedImportsQueryOptions(api, quoteId)
  const imports = useQuery({
    ...options,
    refetchInterval: (query) =>
      query.state.data?.some((item) => item.status === 'awaiting_upload') ? READING_REFRESH_MS : false,
  })
  const discard = useMutation({
    // Already discarded (another tab) is as good as discarded.
    mutationFn: (importId: string) =>
      discardImport(api, importId).catch((error: unknown) => {
        if (!(error instanceof QuoteBuilderApiError && error.status === 404)) throw error
      }),
    onMutate: async (importId) => {
      await client.cancelQueries({ queryKey: options.queryKey })
      const previous = client.getQueryData(options.queryKey)
      client.setQueryData(options.queryKey, (list = []) => list.filter((item) => item.id !== importId))
      return { previous }
    },
    onError: (_error, _importId, context) => client.setQueryData(options.queryKey, context?.previous),
    onSettled: () => client.invalidateQueries({ queryKey: options.queryKey }),
  })
  const items = imports.data ?? []
  if (!items.length && !discard.isError) return null
  return (
    <section aria-label="Unfinished imports" className="overflow-hidden rounded-xl border bg-card">
      <ul className="divide-y">
        {items.map((item) => (
          <ImportRow
            discarding={discard.isPending && discard.variables === item.id}
            item={item}
            key={item.id}
            onDiscard={() => discard.mutate(item.id)}
            onResume={() => onResume(item.id)}
          />
        ))}
      </ul>
      {discard.isError ? (
        <p className="border-t px-4 py-2.5 text-sm font-medium text-destructive" role="alert">
          The import could not be discarded. Try again.
        </p>
      ) : null}
    </section>
  )
}
